import { useState } from 'react'
import { filterTasks, tsToDate, fmtDate, statusLabel } from '../lib/format.js'
import { DataTable, Chip, Avatar } from '../ds/index.js'

const PHASE_LABEL = { p1: '30', p2: '60', p3: '90' }

export default function TableView({
  tasks,
  labelFilter,
  assigneeFilter,
  phaseFilter,
  incompleteOnly,
  dueSoonOnly,
  search,
  statusesById,
  usersById,
  labelsById,
  onOpenTask,
}) {
  // Sort state shape adapted for the kit DataTable contract: { key, dir: 'asc'|'desc' }
  // (was { key, dir: 1|-1 }). Same 2-state cycle as before: clicking a new column
  // sorts it ascending; clicking the active column flips asc<->desc. There is no
  // third "unsorted" state — this preserves the pre-migration behavior exactly.
  const [sort, setSort] = useState({ key: null, dir: 'asc' })

  const filtered = filterTasks(tasks, { labelFilter, assigneeFilter, phaseFilter, incompleteOnly, dueSoonOnly, search, statusesById })

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  }

  const sortValue = (t, key) => {
    switch (key) {
      case 'title':
        return (t.title || '').toLowerCase()
      case 'assigneeUid':
        return (usersById[t.assigneeUid]?.name || '').toLowerCase()
      case 'status':
        return statusesById?.[t.status]?.order ?? 0
      case 'dueDate': {
        const d = tsToDate(t.dueDate)
        return d ? d.getTime() : Infinity
      }
      case 'phase':
        return t.phase || ''
      default:
        return ''
    }
  }

  let rows = filtered
  if (sort.key) {
    const dirMul = sort.dir === 'asc' ? 1 : -1
    rows = [...filtered].sort((a, b) => {
      const av = sortValue(a, sort.key)
      const bv = sortValue(b, sort.key)
      if (av < bv) return -1 * dirMul
      if (av > bv) return 1 * dirMul
      return 0
    })
  }

  const columns = [
    { key: 'title', header: 'Title', priority: 1, sortable: true, render: (t) => t.title },
    {
      key: 'assigneeUid',
      header: 'Assignee',
      priority: 2,
      sortable: true,
      render: (t) => {
        const assignee = usersById[t.assigneeUid]
        return assignee ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar user={assignee} size={20} />
            <span>{assignee.name}</span>
          </div>
        ) : (
          <span style={{ color: 'var(--color-muted)' }}>—</span>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      priority: 2,
      sortable: true,
      render: (t) => statusLabel(t.status, Object.values(statusesById || {})),
    },
    {
      key: 'dueDate',
      header: 'Due',
      priority: 3,
      sortable: true,
      render: (t) => {
        const due = tsToDate(t.dueDate)
        return due ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{fmtDate(due)}</span>
        ) : (
          <span style={{ color: 'var(--color-muted)' }}>—</span>
        )
      },
    },
    {
      key: 'phase',
      header: 'Phase',
      priority: 3,
      sortable: true,
      render: (t) => (t.phase ? `Phase · ${PHASE_LABEL[t.phase] || t.phase}` : <span style={{ color: 'var(--color-muted)' }}>—</span>),
    },
    {
      key: 'labels',
      header: 'Labels',
      priority: 3,
      render: (t) => (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {(t.labelIds || []).map((id) =>
            labelsById[id] ? (
              <Chip key={id} variant="solid" color={labelsById[id].color}>
                {labelsById[id].name}
              </Chip>
            ) : null,
          )}
        </div>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(t) => t.id}
      onRowClick={(t) => onOpenTask(t.id)}
      sort={sort}
      onSort={toggleSort}
    />
  )
}
